import { goblinProportions, goblinRoleProportions, goblinStandardHeight } from '../../../content/characters/rigs/goblin.js';
import { mix } from '../../../content/characters/rigs/human.js';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { createHumanoidModel, type HumanModelOptions } from './human-model.js';
import { goblinTeeth } from './goblin-head.js';
import { addBugbearFur } from './bugbear-fur.js';
import * as THREE from 'three';
import { mergeRigidParts } from './rigid-batches.js';
import { finishWornSurfaces } from './worn-surfaces.js';

export function createGoblinModel(definition: UnitDefinition, options?: HumanModelOptions) {
  const look = definition.appearance!;
  const role = look.parts?.['goblin-role'], p = goblinRoleProportions(goblinProportions(look.rig, look.parts?.face), role);
  const rig = createHumanoidModel({ ...definition, appearance: { ...look, height: look.height ?? goblinStandardHeight(p.id) } }, p, p.id === 'bugbear' ? { ...options, paletteSkin: false } : options,
    { face: p.face, features: goblinTeeth, palette: { eye: 0xb49a52, lips: mix(look.skin, p.id === 'bugbear' ? 0x3a2219 : 0x3b3324, .42) } });
  if (!options?.headless && p.id === 'bugbear') addBugbearFur(rig, look.hair ?? 0x63412b);
  if (!options?.headless && (role === 'bugbear' || role === 'taskmaster')) {
    const iron = new THREE.MeshStandardMaterial({ color: look.metal, metalness: .35, roughness: .85 }); iron.name = 'metal';
    for (const arm of [rig.leftArm, rig.rightArm]) {
      const side = arm === rig.leftArm ? -1 : 1;
      const armor = new THREE.Group(); armor.name = `goblin-${role}-armor`; arm.upper.add(armor);
      const add = (geometry: THREE.BufferGeometry, x: number, y: number, z: number) => {
        const mesh = new THREE.Mesh(geometry, iron); mesh.position.set(x, y, z); mesh.castShadow = mesh.receiveShadow = true; armor.add(mesh); return mesh;
      };
      // Scavenged, overlapping caps follow the upper arm. The leader's narrow lamellae and the brute's battered spikes read differently.
      for (let layer = 0; layer < 2; layer++) {
        const plate = add(new THREE.SphereGeometry(1, 12, 6, 0, Math.PI * 2, 0, Math.PI * .65), side * .012, -.012 - layer * .035, 0);
        const width = role === 'bugbear' ? (side < 0 ? .145 : .125) : .092;
        plate.scale.set(width - layer * .012, .055, role === 'bugbear' ? .115 : .086);
        plate.rotation.z = side * (.20 + layer * .06);
      }
      if (role === 'bugbear') for (let i = 0; i < 2; i++) {
        const length = i === 0 ? .075 : .055;
        const spike = add(new THREE.ConeGeometry(.018, length, 5), side * (.035 + i * .04), .05 + length / 2, -.025);
        spike.rotation.z = -side * (.35 + i * .2);
      }
      finishWornSurfaces(armor);
    }
  }
  mergeRigidParts(rig.root);
  return rig;
}
